import React from "react";
import { Link } from "react-router-dom";
import { Star, Sparkles, Zap, MapPin, Clock, Video } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import FavoriteButton from "@/components/marketplace/FavoriteButton";

const PRICING_LABEL = {
  fixed: "Fixed",
  hourly: "/hr",
  starting_price: "starting",
  price_range: "range",
  package_pricing: "from",
  quote_required: "Quote",
  free_consultation: "Free",
  recurring: "/mo",
};

function formatPrice(s) {
  const cur = s.currency || "USD";
  const symbol = cur === "USD" ? "$" : "";
  if (s.pricing_model === "quote_required") return "Quote";
  if (s.pricing_model === "free_consultation") return "Free";
  if (s.pricing_model === "hourly" && s.hourly_rate) return `${symbol}${s.hourly_rate}/hr`;
  if (s.pricing_model === "recurring") return `${symbol}${s.base_price}/mo`;
  if (s.pricing_model === "starting_price") return `From ${symbol}${s.starting_price ?? s.base_price}`;
  if (s.pricing_model === "package_pricing") return `From ${symbol}${s.base_price}`;
  return `${symbol}${s.base_price}`;
}

export default function ServiceCard({ service, index = 0, sponsored = false }) {
  return (
    <div className="relative">
      <div className="absolute top-3 right-3 z-10">
        <FavoriteButton kind="service" targetId={service.id} initial={!!service.is_favorite} />
      </div>
      <Link
        to={`/services/${service.slug || service.id}`}
        data-testid={`service-card-${service.slug || service.id}`}
        className="group rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-[#165a71]/40 hover:shadow-[0_20px_40px_rgba(7,27,34,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300 animate-fade-up flex flex-col"
        style={{ animationDelay: `${index * 30}ms` }}
      >
      <div className="relative aspect-[4/3] overflow-hidden bg-slate-100">
        {service.featured_image ? (
          <img
            src={service.featured_image}
            alt={service.title}
            className="absolute inset-0 h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-[#165a71]/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {sponsored && (
            <Badge className="rounded-full bg-amber-100 text-amber-800 border border-amber-200 backdrop-blur">
              Sponsored
            </Badge>
          )}
          {service.is_featured && !sponsored && (
            <Badge className="rounded-full bg-[#071b22] text-white border border-white/10">
              <Sparkles className="h-3 w-3 mr-1" /> Featured
            </Badge>
          )}
          {service.instant_accept && (
            <Badge className="rounded-full bg-emerald-500/95 text-white border border-white/10">
              <Zap className="h-3 w-3 mr-1" /> Instant book
            </Badge>
          )}
          {service.kind === "digital" && (
            <Badge className="rounded-full bg-white/95 text-[#165a71] border border-[#165a71]/20">
              <Video className="h-3 w-3 mr-1" /> Digital
            </Badge>
          )}
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white text-xs">
          <span className="inline-flex items-center gap-1 font-medium">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
            {(service.rating_avg || 0).toFixed(1)}
            <span className="text-white/80">({service.rating_count || 0})</span>
          </span>
          <span className="rounded-full bg-white/95 text-[#071b22] px-2.5 py-1 font-display font-semibold">
            {formatPrice(service)}
          </span>
        </div>
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div translate="no" className="font-display text-[15px] leading-snug font-semibold text-[#071b22] line-clamp-2">
          {service.title}
        </div>
        <div translate="no" className="text-xs text-[#252A34]/60 line-clamp-2 min-h-[2rem]">
          {service.short_description}
        </div>
        <div className="mt-auto pt-2 flex items-center gap-3 text-[11px] text-[#252A34]/60">
          {service.same_day_availability && (
            <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Same-day</span>
          )}
          {service.location_ids?.length > 0 && service.kind !== "digital" && (
            <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> On-site</span>
          )}
          {service.distance_km !== undefined && (
            <span className="ml-auto text-[#165a71] font-medium">{service.distance_km.toFixed(1)} km away</span>
          )}
        </div>
      </div>
    </Link>
    </div>
  );
}
